import type { GameEvent } from '@/types'
import { useAPI } from '@/utils/requests/api-plugin'
import type { FetchRequest } from '@/utils/requests/types'

import { MessageAction, type OutcomingMessage } from '../main-frame/message-types'
import { createIdbApi } from '../safe-storage'

const SAFE_STORAGE_TIMEOUT = 5000 // 5s

export type SerializableState = Record<string, string | number | boolean>

const cloudSaveIdbNamespace = (gameHru: string) => `cloud-save:${gameHru}`
export type CloudSaveMessageHandler = (event: GameEvent) => Promise<OutcomingMessage | undefined>

export const useCloudSave = () => {
    const api = useAPI()
    const waitTimeout = <T>(msg: T) => new Promise<T>((res) => setTimeout(() => res(msg), SAFE_STORAGE_TIMEOUT))
    type RequestParams = Parameters<FetchRequest>
    const requestWithTimeout = <T>(url: RequestParams[0], opts?: RequestParams[1]) =>
        Promise.race([
            api.request<T>(url, opts),
            waitTimeout({ originalResponse: undefined, data: null, errors: ['Client timeout'] }),
        ])

    const getCloudSaveApi = (gameId: string) => ({
        getState: async () => {
            const state = await requestWithTimeout<{ saveData: Record<string, string> }>(
                `/api/v1/cloud-save/games/${encodeURIComponent(gameId)}/load`,
            )

            // Throw instead of returning {}: an error must never look like an empty save,
            // or a later save would overwrite the real cloud state
            if (state?.errors?.length) {
                throw new Error(`Cloud save load failed: ${state.errors.join(', ')}`)
            }

            return state.data?.saveData || {}
        },
        setItems: async (state: SerializableState) => {
            const resp = await requestWithTimeout(`/api/v1/cloud-save/games/${encodeURIComponent(gameId)}/save`, {
                method: 'POST',
                body: JSON.stringify({ saveData: state }),
            })

            return !resp.errors?.length
        },
    })
    const createCloudSave = (
        gameId: string,
        gameHru: string,
        onSave: (data: { type: 'cloud-save'; action: MessageAction }) => void,
        { isGuest }: { isGuest: () => Promise<boolean> },
    ) => {
        const cloudSaveApi = getCloudSaveApi(gameId)
        // The unprefixed store is shared with the storage API, so cloud saves own a namespace of
        // their own: a whole-state replace here must not wipe keys the game wrote through storage
        const idbApi = createIdbApi(cloudSaveIdbNamespace(gameHru))
        const sharedIdbApi = createIdbApi(gameHru)

        const idbAdapter = {
            load: async () => {
                const state = await idbApi.getState()

                // Older saves, and storage API writes, still live in the shared namespace, which
                // production has read this way for a long time. A game that cleared its whole
                // state would read those legacy keys back, since an empty namespace cannot be
                // told apart from one that was never written; no game resets and then reloads,
                // so that is accepted rather than paying for a marker to distinguish them.
                return Object.keys(state).length === 0 ? sharedIdbApi.getState() : state
            },
            save: async (state: SerializableState) => {
                await idbApi.replaceState(state as Record<string, string>)
                return true
            },
        }
        const cloudAdapter = {
            load: async () => {
                const state = await cloudSaveApi.getState()

                // If cloud save is empty, try to get state from indexedDB
                return Object.keys(state).length === 0 ? idbAdapter.load() : state
            },
            save: cloudSaveApi.setItems,
        }

        const messageHandler = async (event: GameEvent) => {
            const createResponse = (
                action: MessageAction,
                payload: { state: SerializableState } | { success: boolean; error?: string },
            ) =>
                ({
                    type: 'cloud-save',
                    originalMessageEvent: event as GameEvent,
                    payload,
                    action,
                }) as OutcomingMessage
            // Only a player known to have no account saves into the frame, so a session that
            // could not be resolved goes to the cloud and is authenticated there, rather than
            // stranding a logged-in player's progress in storage nothing will read again
            const adapter = (await isGuest()) ? idbAdapter : cloudAdapter
            switch (event.data.action) {
                case MessageAction.getCloudSaveState:
                    return createResponse(MessageAction.cloudSaveStateResponse, { state: await adapter.load() })
                case MessageAction.setCloudSaveState: {
                    onSave({ type: 'cloud-save', action: MessageAction.setCloudSaveState })
                    try {
                        const success = await adapter.save(event.data.payload as SerializableState)
                        return createResponse(MessageAction.cloudSaveOperationResponse, { success })
                    } catch (e) {
                        console.error(e)
                        const error = (e as Error).toString()
                        return createResponse(MessageAction.cloudSaveOperationResponse, { success: false, error })
                    }
                }
                default:
            }
            return undefined
        }

        return messageHandler
    }
    return {
        createCloudSave,
        getCloudSaveApi,
    }
}
